import { NgOptimizedImage, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';

import { DrawerService } from './core/drawer/drawer.service';
import { LeftSidenavComponent } from './core/left-sidenav/left-sidenav.component';
import { RightSidenavComponent } from './core/right-sidenav/right-sidenav.component';
import { BreadcrumbComponent } from './core/top-navbar/breadcrumb/breadcrumb.component';
import { TopNavbarComponent } from './core/top-navbar/top-navbar.component';
import { OverlaySpinnerComponent } from './shared/components/overlay-spinner/overlay-spinner.component';
import { ROUTES, HomePath } from './shared/constants/routes';
import { TitleService } from './shared/services/title.service';
import { UserStore } from './shared/store/user.store';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TopNavbarComponent,
    MatSidenavContainer,
    MatSidenav,
    RightSidenavComponent,
    MatSidenavContent,
    OverlaySpinnerComponent,
    RouterOutlet,
    NgOptimizedImage,
    AsyncPipe,
    LeftSidenavComponent,
    BreadcrumbComponent,
    MatButton,
  ],
})
export class AppComponent implements OnInit {
  private readonly store = inject(UserStore);
  private readonly drawerService = inject(DrawerService);
  private readonly titleService = inject(TitleService);
  private readonly router = inject(Router);
  private readonly sidenavContainer = viewChild(MatSidenavContainer);

  title = 'orm-client';
  isIframe = false;
  isMobile = false;

  drawerOpened$ = this.drawerService.opened$;
  leftNavCollapsed$ = this.drawerService.leftNavCollapsed$;
  drawerMode$ = this.drawerService.mode$;
  readonly showHomeActionButton = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => this.isHomeRoute(event.urlAfterRedirects)),
      startWith(this.isHomeRoute(this.router.url))
    ),
    { initialValue: this.isHomeRoute(this.router.url) }
  );

  constructor() {
    this.titleService.updateTitleOnNavigationChanges();
  }

  ngOnInit(): void {
    this.isIframe = globalThis.window !== globalThis.window.parent && !globalThis.window.opener;
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  syncLeftSidenavMargins(propertyName: string): void {
    if (propertyName !== 'width' && propertyName !== 'min-width') {
      return;
    }

    this.sidenavContainer()?.updateContentMargins();
  }

  onRaiseIssueEventButtonClick(): void {
    this.router.navigate([`/${ROUTES.wizard.root}/${ROUTES.wizard.selection}`], {
      replaceUrl: true,
    });
  }

  private isHomeRoute(url: string): boolean {
    const firstSegment = url.replace(/^\//, '').split('?')[0].split('/')[0];

    return firstSegment === ROUTES.home || Object.values(HomePath).includes(firstSegment as HomePath);
  }
}
